"use client";
import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { buildFaqSchema, stringifyJsonLd } from "@/utils/faqSchema";

export default function FAQSection({
  course,
  title = "Frequently Asked Questions",
  subtitle = "Find quick answers to the most common questions about our programme"
}) {

  const [openItem, setOpenItem] = useState(null);
  const [windowWidth, setWindowWidth] = useState(0);

  const faqData = course?.FAQ || [];
  const faqSchema = buildFaqSchema(faqData);


  useEffect(() => {
    if (faqData.length > 0 && openItem === null) {
      setOpenItem(faqData[0].id);
    }
  }, [faqData, openItem]);


  useEffect(() => {
    if (typeof window !== "undefined") {
      setWindowWidth(window.innerWidth);
    }

    const handleResize = () => {
      if (typeof window !== "undefined") {
        setWindowWidth(window.innerWidth);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggleItem = (id) => {
    setOpenItem(openItem === id ? null : id);
  };

  return (
    <section className="page-container bg-white my-12.5 md:my-20 lg:my-25">
      {faqSchema && (
        <script
          id="FAQSchema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: stringifyJsonLd(faqSchema) }}
        />
      )}

      <div className="text-center mb-3 md:mb-5">
        <h2
          className="mb-3 tracking-[-1.2px] sm:text-[42px] text-[24px] text-left"
          style={{ fontFamily: "saans-bold" }}
        >
          {title}
        </h2>
        <p
          className="text-[black] leading-5.75 sm:text-[18px] text-base text-left mb-7.5 md:mb-12"
          style={{ fontFamily: "saans-regular" }}
        >
          {subtitle}
        </p>
      </div>

      <div className="">
        {faqData.map((item, index) => {
          const itemId = item.id;
          const isOpen = openItem === itemId;

          const displayNumber = String(index + 1).padStart(2, '0');

          return (
            <motion.div
              key={itemId}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div
                className={`border transition-all duration-500 ease-out mb-3
                  ${isOpen
                    ? "bg-white border-blue-200/50 shadow-2xl shadow-blue-500/10 scale-[1.01] xs:scale-[1.02] ring-1 ring-blue-100/50"
                    : "bg-[#F9F9FD] border-gray-200/40 shadow-lg shadow-gray-500/5 hover:bg-white hover:border-blue-200/30 hover:shadow-xl hover:shadow-blue-500/8 hover:scale-[1.005] xs:hover:scale-[1.01] hover:ring-1 hover:ring-blue-100/30"
                  }
                `}
              >
                <div
                  className={`flex items-center justify-between px-3 py-2 md:px-9 md:py-2 cursor-pointer group`}
                  onClick={() => toggleItem(itemId)}
                >
                  <div className="flex items-center grow min-w-0 mr-2 xs:mr-3 sm:mr-4">
                    <div
                      className={`
                        hidden md:flex transition-all duration-300 ease-in-out shrink-0 mr-7 text-[24px] text-[#979CB1]
                      `}
                    >
                      {displayNumber}
                    </div>

                    <p
                      className="transition-all duration-300 ease-in-out text-black text-[20px] py-4"
                      style={{ fontFamily: "saans-medium" }}
                    >
                      {item.Question}
                    </p>
                  </div>

                  <motion.button
                    type="button"
                    aria-label={isOpen ? "Collapse answer" : "Expand answer"}
                    aria-expanded={isOpen}
                    className={`
                      w-8 h-8 xs:w-9 xs:h-9 sm:w-10 sm:h-10 md:w-12 md:h-12 
                      rounded-full border-none transition-all duration-300 ease-in-out
                      flex items-center justify-center shrink-0
                      ${isOpen
                        ? "bg-[#0475B3] text-white shadow-lg shadow-blue-600/25 ring-1 xs:ring-2 ring-blue-200/50"
                        : "bg-white text-slate-400 shadow-md shadow-gray-500/10 hover:bg-[#0475B3] hover:text-white hover:shadow-lg hover:shadow-[#0475B3] hover:ring-1 xs:hover:ring-2 hover:ring-[#0475B3] ring-1 ring-gray-200/30"
                      }
                    `}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <AnimatePresence mode="wait">
                      {isOpen ? (
                        <motion.div
                          key="minus"
                          initial={{ opacity: 0, rotate: -90 }}
                          animate={{ opacity: 1, rotate: 0 }}
                          exit={{ opacity: 0, rotate: 90 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Minus size={windowWidth < 640 ? 14 : 18} strokeWidth={2.5} />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="plus"
                          initial={{ opacity: 0, rotate: 90 }}
                          animate={{ opacity: 1, rotate: 0 }}
                          exit={{ opacity: 0, rotate: -90 }}
                          transition={{ duration: 0.5 }}
                        >
                          <Plus size={windowWidth < 640 ? 14 : 18} strokeWidth={2.5} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.button>
                </div>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4, ease: "easeInOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <div className="">
                        <div className="">
                          <motion.p
                            className="mb-7.5 sm:text-[16px] text-base text-[#3D677E] text-[14px] leading-5 px-3.5 md:px-22"
                            initial={{ y: -10, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.3, delay: 0.1 }}
                            style={{ fontFamily: "saans-regular" }}
                          >
                            {item.Answer}
                          </motion.p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
